import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Checkbox,
  FormControlLabel,
  Stack,
  Divider,
  Link as MuiLink,
} from '@mui/material';
import { AppRegistration as RegisterIcon, CheckCircle } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { analysisFindingsAPI } from '../../services/api';
import { getSampleColumns, renderSampleRow } from './sampleFindings';
import { isSimpleRoleKind, SubChoice, DirectRolePickers } from './columnMapping';

/**
 * Pipeline registration page (#37).
 *
 * Form-driven registration flow that reads its column-role choices and
 * smart-inference rules from the result type catalog
 * (GET /api/analysis-findings/result-types). Progressive reveal — later
 * sections enable as earlier ones complete. Researcher's path:
 *
 *   module → result type → identity → sample file → column mapping
 *   → dry-run preview → register
 *
 * Single-file pipelines only in v1 (the parser declaration's
 * `file_key: 'main'` slot). Multi-file pipelines deferred until a real
 * use case lands.
 */

// Client-side mirror of modules/_kinds/index.js:inferColumnRole — the
// catalog ships its rules via /result-types, the form applies them here.
function inferColumnRole(columnName, columnInference) {
  if (!columnName) return null;
  const lower = columnName.toLowerCase();
  for (const rule of columnInference || []) {
    for (const pattern of rule.match || []) {
      if (lower.includes(pattern.toLowerCase())) {
        // eslint-disable-next-line no-unused-vars
        const { match, ...rest } = rule;
        return rest;
      }
    }
  }
  return null;
}

function isLikelyIdentifierColumn(columnName, patterns) {
  if (!columnName) return false;
  const lower = columnName.toLowerCase();
  return (patterns || []).some((p) => lower.includes(p.toLowerCase()));
}

// Client-side mirror of modules/_kinds/index.js:inferKindFromColumns. Assigns
// each column to the most specific kind that claims it, then auto-picks only
// when exactly one kind matches; two-or-more (genuinely mixed) => ask. Returns
// { kind: string|null, confident: boolean }. `resultTypes` is
// catalog.result_types (carries each kind's detection signature).
function inferKindFromColumns(headers, candidateKinds, resultTypes) {
  const lowers = (headers || []).map((h) => String(h).toLowerCase());
  const names = candidateKinds && candidateKinds.length ? candidateKinds : Object.keys(resultTypes || {});
  if (names.length === 0) return { kind: null, confident: false };

  const matched = new Set();
  for (const col of lowers) {
    let claim = null;
    for (const name of names) {
      const det = (resultTypes[name] && resultTypes[name].detection) || { priority: 0, signals: [] };
      const signals = det.signals || [];
      if (signals.length === 0) continue;
      const hit = signals.some((s) => col.includes(String(s).toLowerCase()));
      if (hit && (!claim || (det.priority || 0) > claim.priority)) {
        claim = { name, priority: det.priority || 0 };
      }
    }
    if (claim) matched.add(claim.name);
  }

  if (matched.size === 1) return { kind: [...matched][0], confident: true };
  if (matched.size > 1) return { kind: null, confident: false };

  const fallback = names.find((n) => {
    const sig = resultTypes[n] && resultTypes[n].detection && resultTypes[n].detection.signals;
    return !(sig && sig.length);
  });
  return fallback ? { kind: fallback, confident: true } : { kind: null, confident: false };
}

// Parse the header row of a TSV File. Reads only enough text to find the
// first non-empty line.
async function parseTsvHeader(file) {
  const text = await file.text();
  const firstLine = text.split(/\r?\n/).find((line) => line.trim().length > 0);
  if (!firstLine) return [];
  return firstLine.split('\t');
}

const PipelineRegistration = () => {
  const navigate = useNavigate();

  const [catalog, setCatalog] = useState(null);
  const [catalogError, setCatalogError] = useState(null);

  // Section 1 + 2: module + result type
  const [moduleName, setModuleName] = useState('');
  const [resultType, setResultType] = useState('');
  // True when the result type below was auto-detected from the uploaded file's
  // header (vs. picked by the user) — drives the "Detected from your file" hint.
  const [typeAutoDetected, setTypeAutoDetected] = useState(false);
  // True when detection couldn't confidently pick (the file's columns matched
  // more than one result type, or none) — we ask instead of guessing.
  const [typeAmbiguous, setTypeAmbiguous] = useState(false);

  // Section 3: identity
  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [version, setVersion] = useState('1.0.0');
  const [description, setDescription] = useState('');

  // Section 4: sample file
  const [sampleFile, setSampleFile] = useState(null);
  const [sampleHeader, setSampleHeader] = useState([]);
  const [headerError, setHeaderError] = useState(null);

  // Section 5: column mapping
  // assignments: { [columnName]: { role: string|null, taxonomy_rank?: string, kind?: string } }
  const [assignments, setAssignments] = useState({});
  const [databaseIdColumn, setDatabaseIdColumn] = useState('');

  // Section 6: preview
  const [preview, setPreview] = useState({
    status: 'idle', // 'idle' | 'previewing' | 'ok' | 'issues' | 'error'
    result: null,
    error: null,
  });
  const previewRequestRef = useRef(0);

  // Section 7: register
  const [registering, setRegistering] = useState(false);
  const [registerResult, setRegisterResult] = useState(null);

  // Load the catalog once on mount.
  useEffect(() => {
    let cancelled = false;
    analysisFindingsAPI
      .getResultTypes()
      .then((res) => {
        if (cancelled) return;
        setCatalog(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        setCatalogError(err.response?.data?.msg || err.message || 'Failed to load result types');
      });
    return () => { cancelled = true; };
  }, []);

  const moduleEntries = useMemo(
    () => (catalog ? Object.entries(catalog.modules || {}) : []),
    [catalog],
  );

  const selectedModule = moduleName && catalog ? catalog.modules[moduleName] : null;
  const moduleKinds = selectedModule ? selectedModule.kinds : [];
  const selectedResultType = resultType && catalog ? catalog.result_types[resultType] : null;

  // The common multi-kind case: a module that offers both the generic
  // "Specimen data" kind (classification) and taxonomy. There, taxonomy is an
  // explicit opt-in checkbox rather than an abstract "pick a result type."
  const taxonomyOptIn = moduleKinds.includes('taxon') && moduleKinds.includes('classification');

  // When a module is picked, default the result type. Single-kind modules pin
  // to their one kind; multi-kind modules default to the generic "Specimen
  // data" case when available (taxonomy is the opt-in below), else first kind.
  useEffect(() => {
    if (!selectedModule) return;
    setTypeAutoDetected(false); // a fresh module pick — any prior detection is stale
    setTypeAmbiguous(false);
    if (moduleKinds.length === 1) {
      setResultType(moduleKinds[0]);
    } else if (!moduleKinds.includes(resultType)) {
      setResultType(moduleKinds.includes('classification') ? 'classification' : (moduleKinds[0] || ''));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleName]);

  // When a sample file is selected, parse its header + apply inference.
  useEffect(() => {
    if (!sampleFile) {
      setSampleHeader([]);
      setHeaderError(null);
      return;
    }
    setHeaderError(null);
    parseTsvHeader(sampleFile)
      .then((cols) => {
        if (cols.length === 0) {
          setHeaderError('No header row detected — is this a TSV with column names on the first line?');
          setSampleHeader([]);
          return;
        }
        setSampleHeader(cols);
        // For multi-kind modules, pre-select the result type from the file's
        // columns ONLY when detection is confident (exactly one kind matches).
        // When the file is mixed/unclear, clear the type and ask rather than
        // guess. Single-kind modules are already pinned.
        if (moduleKinds.length > 1 && catalog) {
          const { kind: detected, confident } = inferKindFromColumns(cols, moduleKinds, catalog.result_types);
          if (confident && detected) {
            setResultType(detected);
            setTypeAutoDetected(true);
            setTypeAmbiguous(false);
          } else {
            setResultType('');
            setTypeAutoDetected(false);
            setTypeAmbiguous(true);
          }
        }
      })
      .catch((e) => {
        setHeaderError(`Failed to read file: ${e.message}`);
        setSampleHeader([]);
      });
  // Keyed on sampleFile only — catalog/moduleKinds are stable by the time a
  // file is chosen (module is picked first); re-running on their identity would
  // re-parse the file needlessly.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sampleFile]);

  // Apply smart inference whenever the sample header or result type changes.
  // For single-use roles (multi:false), only the first matching column gets
  // the role — subsequent matches stay unmapped so the user isn't given an
  // invalid registration that would have to be corrected manually.
  useEffect(() => {
    if (sampleHeader.length === 0 || !selectedResultType || !catalog) return;
    const newAssignments = {};
    let inferredId = '';
    const rolesByKey = Object.fromEntries(
      (selectedResultType.registration_roles || []).map((r) => [r.key, r])
    );
    const usedSingleUseRoles = new Set();

    for (const col of sampleHeader) {
      if (!inferredId && isLikelyIdentifierColumn(col, catalog.identifier_column_patterns)) {
        inferredId = col;
        continue;
      }
      const suggestion = inferColumnRole(col, selectedResultType.column_inference);
      if (!suggestion) {
        newAssignments[col] = { role: null };
        continue;
      }
      const roleSpec = rolesByKey[suggestion.role];
      const isSingleUse = roleSpec && roleSpec.multi === false;
      if (isSingleUse && usedSingleUseRoles.has(suggestion.role)) {
        // Another column already claimed this single-use role; leave this
        // column unmapped rather than producing an invalid duplicate.
        newAssignments[col] = { role: null };
        continue;
      }
      newAssignments[col] = {
        role: suggestion.role,
        taxonomy_rank: suggestion.taxonomy_rank,
        kind: suggestion.kind,
      };
      if (isSingleUse) usedSingleUseRoles.add(suggestion.role);
    }
    setAssignments(newAssignments);
    setDatabaseIdColumn(inferredId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sampleHeader, resultType]);

  // Reset preview whenever anything affecting the dry-run changes.
  useEffect(() => {
    setPreview({ status: 'idle', result: null, error: null });
  }, [moduleName, resultType, sampleFile, assignments, databaseIdColumn]);

  // ---------------------------------------------------------------------------
  // Derived state — section gating.
  // ---------------------------------------------------------------------------

  const identityValid = name.trim() && displayName.trim() && version.trim();
  const mappingReady =
    sampleHeader.length > 0 &&
    databaseIdColumn &&
    sampleHeader.includes(databaseIdColumn);

  const annotationsForSubmit = useMemo(() => {
    return Object.entries(assignments)
      .filter(([col, a]) => a.role && col !== databaseIdColumn)
      .map(([col, a]) => ({
        column: col,
        role: a.role,
        taxonomy_rank: a.taxonomy_rank,
        kind: a.kind,
      }));
  }, [assignments, databaseIdColumn]);

  // ---------------------------------------------------------------------------
  // Preview + register actions.
  // ---------------------------------------------------------------------------

  const buildPreviewFormData = () => {
    const fd = new FormData();
    fd.append('module', moduleName);
    fd.append('result_type', resultType);
    fd.append('database_id_column', databaseIdColumn);
    fd.append('expected_columns', JSON.stringify(sampleHeader));
    fd.append('annotations', JSON.stringify(annotationsForSubmit));
    if (sampleFile) fd.append('main', sampleFile, sampleFile.name);
    return fd;
  };

  const handlePreview = async () => {
    const reqId = ++previewRequestRef.current;
    setPreview({ status: 'previewing', result: null, error: null });
    try {
      const res = await analysisFindingsAPI.registerPreview(buildPreviewFormData());
      if (previewRequestRef.current !== reqId) return;
      setPreview({
        status: res.data.ok ? 'ok' : 'issues',
        result: res.data,
        error: null,
      });
    } catch (err) {
      if (previewRequestRef.current !== reqId) return;
      const data = err.response?.data;
      if (data && (data.issues || data.specimen_mapping)) {
        setPreview({ status: 'issues', result: data, error: data.msg || null });
      } else {
        setPreview({
          status: 'error',
          result: null,
          error: data?.msg || err.message || 'Preview failed',
        });
      }
    }
  };

  const handleRegister = async () => {
    setRegistering(true);
    try {
      const res = await analysisFindingsAPI.register({
        module: moduleName,
        result_type: resultType,
        name,
        display_name: displayName,
        version,
        description,
        database_id_column: databaseIdColumn,
        expected_columns: sampleHeader,
        annotations: annotationsForSubmit,
      });
      setRegisterResult(res.data);
      toast.success(`Registered ${res.data.name} @ ${res.data.version}`);
    } catch (err) {
      const data = err.response?.data;
      if (data?.errors) {
        toast.error(`Registration failed: ${data.errors.join('; ')}`);
      } else {
        toast.error(data?.msg || err.message || 'Registration failed');
      }
    } finally {
      setRegistering(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (catalogError) {
    return (
      <Box sx={{ p: 3, maxWidth: 1100 }}>
        <Alert severity="error">Failed to load result types: {catalogError}</Alert>
      </Box>
    );
  }
  if (!catalog) {
    return (
      <Box sx={{ p: 3 }}>
        <CircularProgress size={20} /> <Typography variant="body2" component="span">Loading…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1100 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <RegisterIcon color="primary" />
        <Typography variant="h5">Register a pipeline</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Tell the system what your analysis pipeline produces, so its output files can be ingested by researchers
        without writing code. Pick the module and result type, upload a sample file, and map its columns.
      </Typography>

      {/* SECTION 1: module */}
      <Paper sx={{ p: 3, mb: 2 }} elevation={1}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>1. Module</Typography>
        <FormControl fullWidth size="small">
          <InputLabel>Module</InputLabel>
          <Select
            value={moduleName}
            label="Module"
            onChange={(e) => setModuleName(e.target.value)}
          >
            {moduleEntries.map(([key, mod]) => (
              <MenuItem key={key} value={key}>{mod.display_name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {/* SECTION 2: what's in the file */}
      {selectedModule && (
        <Paper sx={{ p: 3, mb: 2 }} elevation={1}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>2. What's in your file?</Typography>
          {moduleKinds.length === 1 ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip label={catalog.result_types[moduleKinds[0]]?.display_name || moduleKinds[0]} color="primary" size="small" />
              <Typography variant="caption" color="text.secondary">
                — this module produces only this result type.
              </Typography>
            </Stack>
          ) : taxonomyOptIn ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                By default each row is treated as <strong>specimen data</strong> — values about a
                specimen (measurements, scores, a category), with every column saved. Check the box
                only if each row is an organism identified in a specimen.
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={resultType === 'taxon'}
                    onChange={(e) => setResultType(e.target.checked ? 'taxon' : 'classification')}
                  />
                }
                label="This file contains taxonomy — organisms identified per specimen (e.g. a 16S feature table or a Kraken report)"
              />
            </>
          ) : (
            <>
              {/* Fallback for any future multi-kind module that isn't the
                  taxon + specimen-data pair: a plain result-type picker. */}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                What type of result does this pipeline's file contain?
              </Typography>
              {typeAmbiguous && (
                <Typography variant="caption" color="warning.main" sx={{ mb: 1, display: 'block' }}>
                  This file's columns matched more than one result type (or none) — please pick the
                  right one below.
                </Typography>
              )}
              <FormControl fullWidth size="small">
                <InputLabel>Result type</InputLabel>
                <Select
                  value={resultType}
                  label="Result type"
                  onChange={(e) => { setResultType(e.target.value); setTypeAutoDetected(false); setTypeAmbiguous(false); }}
                >
                  {moduleKinds.map((k) => (
                    <MenuItem key={k} value={k}>
                      {catalog.result_types[k]?.display_name || k}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {typeAutoDetected && resultType && (
                <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
                  Detected <strong>{catalog.result_types[resultType]?.display_name || resultType}</strong> from your
                  file — change it above if that's not right.
                </Typography>
              )}
              {resultType && catalog.result_types[resultType]?.description && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {catalog.result_types[resultType].description}
                </Typography>
              )}
            </>
          )}
        </Paper>
      )}

      {/* SECTION 3: identity */}
      {resultType && (
        <Paper sx={{ p: 3, mb: 2 }} elevation={1}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>3. Pipeline identity</Typography>
          <Stack spacing={2}>
            <TextField
              label="Name (globally unique slug)"
              size="small"
              fullWidth
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              helperText="Lowercase kebab-case, e.g. fresh-16s-dada2-gtdb"
            />
            <TextField
              label="Display name"
              size="small"
              fullWidth
              required
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              helperText="What researchers see in dropdowns, e.g. FRESH 16S — DADA2 + GTDB"
            />
            <TextField
              label="Version"
              size="small"
              fullWidth
              required
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              helperText="Semver-ish, e.g. 1.0.0"
            />
            <TextField
              label="Description (optional)"
              size="small"
              fullWidth
              multiline
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Stack>
        </Paper>
      )}

      {/* SECTION 4: sample file */}
      {resultType && identityValid && (
        <Paper sx={{ p: 3, mb: 2 }} elevation={1}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>4. Sample file</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload one example output file from this pipeline. Its header row defines the columns; its data rows
            are used to dry-run the mapping below.
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Button variant="outlined" component="label" size="small">
              {sampleFile ? sampleFile.name : 'Choose sample TSV'}
              <input
                type="file"
                hidden
                onChange={(e) => setSampleFile(e.target.files?.[0] || null)}
              />
            </Button>
            {sampleFile && (
              <Typography variant="caption" color="text.secondary">
                {(sampleFile.size / 1024).toFixed(1)} KB · {sampleHeader.length} columns detected
              </Typography>
            )}
          </Stack>
          {headerError && (
            <Alert severity="error" sx={{ mt: 2 }}>{headerError}</Alert>
          )}
        </Paper>
      )}

      {/* SECTION 5: column mapping */}
      {sampleHeader.length > 0 && selectedResultType && (
        <Paper sx={{ p: 3, mb: 2 }} elevation={1}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>5. Column mapping</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {(selectedResultType.registration_roles || []).length === 0
              ? 'Pick the column carrying the LIMS specimen number. Every other column is saved as-is.'
              : 'First pick the column carrying the LIMS specimen number, then map the rest.'}
          </Typography>
          <FormControl size="small" sx={{ minWidth: 300, mb: 2 }}>
            <InputLabel>Which column is the specimen identifier?</InputLabel>
            <Select
              label="Which column is the specimen identifier?"
              value={databaseIdColumn || ''}
              onChange={(e) => setDatabaseIdColumn(e.target.value)}
            >
              {sampleHeader.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>
          {(selectedResultType.registration_roles || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nothing else to map — every other column is saved with each specimen.
            </Typography>
          ) : isSimpleRoleKind(selectedResultType.registration_roles) ? (
            <DirectRolePickers
              columns={sampleHeader}
              roles={selectedResultType.registration_roles}
              assignments={assignments}
              setAssignments={setAssignments}
              idColumn={databaseIdColumn}
              onChangeIdColumn={null}
            />
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Column</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Sub-choice</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sampleHeader.map((col) => {
                    const isId = databaseIdColumn === col;
                    const a = assignments[col] || { role: null };
                    return (
                      <TableRow key={col}>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{col}</TableCell>
                        <TableCell>
                          {isId ? (
                            <Chip label="Specimen ID" size="small" />
                          ) : (
                            <FormControl size="small" sx={{ minWidth: 180 }}>
                              <Select
                                value={a.role || ''}
                                onChange={(e) =>
                                  setAssignments((s) => ({
                                    ...s,
                                    [col]: { ...s[col], role: e.target.value || null },
                                  }))
                                }
                                displayEmpty
                              >
                                <MenuItem value=""><em>Unmapped</em></MenuItem>
                                {selectedResultType.registration_roles.map((r) => (
                                  <MenuItem key={r.key} value={r.key}>{r.label}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        </TableCell>
                        <TableCell>
                          {!isId && a.role && (
                            <SubChoice
                              role={selectedResultType.registration_roles.find((r) => r.key === a.role)}
                              assignment={a}
                              onChange={(patch) =>
                                setAssignments((s) => ({ ...s, [col]: { ...s[col], ...patch } }))
                              }
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* SECTION 6: preview + register */}
      {mappingReady && (
        <Paper sx={{ p: 3, mb: 2 }} elevation={1}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>6. Dry-run preview</Typography>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handlePreview}
              disabled={preview.status === 'previewing' || registering}
              startIcon={preview.status === 'previewing' ? <CircularProgress size={14} /> : null}
            >
              {preview.status === 'previewing' ? 'Previewing…' : 'Run preview'}
            </Button>
            {preview.status === 'ok' && (
              <Chip label="OK" color="success" size="small" />
            )}
            {preview.status === 'issues' && (
              <Chip label="Issues" color="warning" size="small" />
            )}
            {preview.status === 'error' && (
              <Typography variant="caption" color="error">Preview failed: {preview.error}</Typography>
            )}
          </Stack>
          {preview.result && <PreviewSummary result={preview.result} resultType={resultType} />}

          {preview.status === 'ok' && !registerResult && (
            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleRegister}
                disabled={registering}
                startIcon={registering ? <CircularProgress size={14} /> : <CheckCircle />}
              >
                {registering ? 'Registering…' : 'Register pipeline'}
              </Button>
            </Box>
          )}
        </Paper>
      )}

      {registerResult && (
        <Paper sx={{ p: 3 }} elevation={1}>
          <Alert severity="success">
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Registered {registerResult.name} @ {registerResult.version}
            </Typography>
            <Typography variant="caption" component="div" sx={{ mb: 1 }}>
              {registerResult.inserted ? 'New registration.' : 'Updated existing registration.'}
            </Typography>
            <MuiLink
              component="button"
              variant="body2"
              onClick={() => navigate('/analysis/upload')}
            >
              Go to upload page →
            </MuiLink>
          </Alert>
        </Paper>
      )}
    </Box>
  );
};

// Inline preview summary — issues, specimen mapping, finding count + sample rows.
function PreviewSummary({ result, resultType }) {
  return (
    <>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Chip label={`Specimens: ${result.specimen_mapping?.matched ?? 0} matched, ${result.specimen_mapping?.unmatched?.length ?? 0} unmatched`} size="small" />
        <Chip label={`Findings: ${result.finding_count ?? 0}`} size="small" />
      </Stack>
      {result.issues && result.issues.length > 0 && (
        <Box sx={{ mb: 2 }}>
          {result.issues.map((iss, i) => (
            <Alert key={i} severity={iss.level === 'error' ? 'error' : 'warning'} sx={{ mb: 0.5 }}>
              {iss.message}
              {iss.row != null && (
                <Typography component="span" variant="caption" sx={{ ml: 1 }}>
                  (row {iss.row})
                </Typography>
              )}
            </Alert>
          ))}
        </Box>
      )}
      {result.specimen_mapping?.unmatched?.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Some example rows don't match LIMS specimens
          </Typography>
          <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
            That's fine for registration — the sample file only teaches the
            parser its columns. Whether rows match real specimens is decided at
            ingest, not here.
          </Typography>
          <Typography variant="caption">
            {result.specimen_mapping.unmatched.join(', ')}
          </Typography>
        </Alert>
      )}
      {result.sample_findings && result.sample_findings.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Sample findings (first {result.sample_findings.length})
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>WUID</TableCell>
                  {getSampleColumns(resultType).map((c) => (
                    <TableCell key={c}>{c}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {result.sample_findings.map((f, i) => (
                  <TableRow key={i}>
                    <TableCell>{f.wuid}</TableCell>
                    {renderSampleRow(f, resultType).map((v, j) => (
                      <TableCell key={j}>{v}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </>
  );
}

export default PipelineRegistration;
